import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { Search, Layers3, Map, ZoomIn, ZoomOut, RotateCcw, Maximize2, Info, Sparkles, Mountain, Wind, Target, Trees, ChevronRight, Image as ImageIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { ImageWithFallback } from '../figma/ImageWithFallback';
import { ModalShell } from './ModalShell';
import { municipalities } from '../../data/municipalities';
import { buildMapLibrary } from '../../utils/mapCatalog';

const TAB_DEFINITIONS = [
  {
    key: 'municipality',
    label: 'Municipality Maps',
    icon: Map,
    accent: 'from-amber-500 to-orange-600',
    description: 'Browse the GIS-exported suitability map for each municipality.',
  },
  {
    key: 'province',
    label: 'Province Overview',
    icon: Mountain,
    accent: 'from-emerald-500 to-green-600',
    description: 'View the Bohol-wide suitability summary map.',
  },
  {
    key: 'factor',
    label: 'Requirement Maps',
    icon: Trees,
    accent: 'from-sky-500 to-cyan-600',
    description: 'Inspect the cacao requirement factor maps used in the analysis.',
  },
  {
    key: 'sampling',
    label: 'Sampling Site',
    icon: Target,
    accent: 'from-violet-500 to-fuchsia-600',
    description: 'Open the sampling-site map used to generate the analysis.',
  },
];

function encodeMapAssetPath(path) {
  if (typeof path === 'string' && path.startsWith('data:')) {
    return path;
  }
  return encodeURI(path).replace(/\(/g, '%28').replace(/\)/g, '%29');
}

function getActiveMap(activeTab, selectedMunicipality, library) {
  if (activeTab === 'province') {
    return library.provinceMaps[0];
  }

  if (activeTab === 'factor') {
    return library.factorMaps[0];
  }

  if (activeTab === 'sampling') {
    return library.samplingMap;
  }

  return library.municipalityLookup[selectedMunicipality] || library.municipalityMaps[0];
}

export function BoholMap({ catalog = [], headerAction = null }) {
  const [activeTab, setActiveTab] = useState('municipality');
  const [displaySearchQuery, setDisplaySearchQuery] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMunicipality, setSelectedMunicipality] = useState('');
  const [selectedFactor, setSelectedFactor] = useState('');
  const [zoom, setZoom] = useState(100);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  const previewRefMain = useRef(null);
  const previewRefModal = useRef(null);
  const searchTimeoutRef = useRef(null);

  const mapLibrary = useMemo(() => buildMapLibrary(catalog), [catalog]);
  const provinceMaps = mapLibrary.provinceMaps;
  const factorMaps = mapLibrary.factorMaps;
  const municipalityMaps = mapLibrary.municipalityMaps;
  const municipalityLookup = mapLibrary.municipalityLookup;
  const suitabilityCollections = mapLibrary.suitabilityCollections;

  useEffect(() => {
    if (activeTab === 'municipality' && municipalityMaps.length > 0 && !municipalityLookup[selectedMunicipality]) {
      setSelectedMunicipality(municipalityMaps[0].municipality);
    }
    if (activeTab === 'factor' && factorMaps.length > 0 && !factorMaps.some((factor) => factor.key === selectedFactor)) {
      setSelectedFactor(factorMaps[0].key);
    }
    if (activeTab === 'province' || activeTab === 'sampling') {
      setZoom(100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedMunicipality, selectedFactor]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const activeMap = useMemo(() => {
    if (activeTab === 'factor') {
      return factorMaps.find((factor) => factor.key === selectedFactor) || factorMaps[0];
    }

    return getActiveMap(activeTab, selectedMunicipality, mapLibrary);
  }, [activeTab, selectedFactor, selectedMunicipality, mapLibrary, factorMaps]);

  const filteredMunicipalities = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return municipalities.filter((name) => name.toLowerCase().includes(query));
  }, [searchQuery]);

  const activeCollection = suitabilityCollections[activeTab] || [];

  const mapStats = [
    { label: 'Municipalities', value: municipalityMaps.length, icon: Map },
    { label: 'Requirement Maps', value: factorMaps.length, icon: Layers3 },
    { label: 'Province Maps', value: provinceMaps.length, icon: Mountain },
    { label: 'Sampling Maps', value: 1, icon: Target },
  ];

  const mapTitle = activeTab === 'municipality'
    ? `${activeMap?.subtitle || selectedMunicipality}`
    : activeMap?.title;

  const mapSubtitle = activeTab === 'municipality'
    ? 'GIS-exported municipal suitability map'
    : activeMap?.description;

  const zoomLabel = `${zoom}%`;

  const zoomIn = useCallback(() => setZoom((current) => Math.min(220, current + 20)), []);
  const zoomOut = useCallback(() => setZoom((current) => Math.max(60, current - 20)), []);
  const resetZoom = useCallback(() => setZoom(100), []);

  const handleMouseDown = useCallback((e) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
  }, []);

  const handleMouseMove = useCallback((ref, e) => {
    setIsDragging((isDrag) => {
      if (!isDrag || !ref.current) return isDrag;

      const startX = dragStart.x;
      const startY = dragStart.y;
      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;

      ref.current.scrollLeft -= deltaX;
      ref.current.scrollTop -= deltaY;

      setDragStart({ x: e.clientX, y: e.clientY });
      return isDrag;
    });
  }, [dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMainMouseMove = useCallback((e) => handleMouseMove(previewRefMain, e), [handleMouseMove]);
  const handleModalMouseMove = useCallback((e) => handleMouseMove(previewRefModal, e), [handleMouseMove]);

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setDisplaySearchQuery(value);
    clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(value);
    }, 150);
  }, []);

  const renderSelectionList = () => {
    if (activeTab === 'municipality') {
      return (
        <>
          <div className="p-4 border-b border-amber-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-amber-500" />
              <input
                type="text"
                value={displaySearchQuery}
                onChange={handleSearchChange}
                placeholder="Search municipality..."
                className="w-full rounded-2xl border border-amber-200 bg-white pl-10 pr-4 py-3 text-sm text-slate-800 outline-none transition focus:border-amber-400 focus:ring-2 focus:ring-amber-200"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-3">
            {municipalityMaps.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                No map records were loaded from the database.
              </div>
            ) : filteredMunicipalities.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                No municipalities match your search.
              </div>
            ) : filteredMunicipalities.map((municipality) => {
              const map = municipalityLookup[municipality];
              const isSelected = selectedMunicipality === municipality || municipalityLookup[selectedMunicipality]?.municipality === municipality;

              return (
                <button
                  key={municipality}
                  type="button"
                  onClick={() => setSelectedMunicipality(municipality)}
                  className={`mb-2 w-full rounded-2xl border p-4 text-left transition-all duration-200 ${
                    isSelected
                      ? 'border-amber-400 bg-amber-50 shadow-md'
                      : 'border-slate-200 bg-white hover:border-amber-200 hover:bg-amber-50/60'
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-xs font-semibold text-slate-900">{municipality}</div>
                      <div className="mt-1 text-[11px] text-slate-500">{map?.subtitle || 'Suitability map available in the GIS folder'}</div>
                    </div>
                    <ChevronRight className={`h-4 w-4 ${isSelected ? 'text-amber-600' : 'text-slate-300'}`} />
                  </div>
                </button>
              );
            })}
          </div>
        </>
      );
    }

    return (
      <div className="flex-1 overflow-y-auto p-3">
        {activeCollection.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
            No map records were loaded from the database.
          </div>
        ) : null}
        {activeCollection.map((entry) => {
          const isSelected = activeTab === 'factor' ? entry.key === selectedFactor : entry.key === activeMap?.key;

          return (
            <button
              key={entry.key}
              type="button"
              onClick={() => {
                if (activeTab === 'factor') {
                  setSelectedFactor(entry.key);
                }
                if (activeTab === 'province') {
                  setZoom(100);
                }
              }}
              className={`mb-2 w-full rounded-2xl border p-4 text-left transition-all duration-200 ${
                isSelected
                  ? 'border-emerald-400 bg-emerald-50 shadow-md'
                  : 'border-slate-200 bg-white hover:border-emerald-200 hover:bg-emerald-50/60'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${isSelected ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-500'}`}>
                  <ImageIcon className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold text-slate-900">{entry.title}</div>
                  <div className="mt-1 text-[11px] text-slate-500">{entry.description}</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    );
  };

  const renderPreview = () => {
    if (!activeMap) {
      return (
        <div className="flex h-full items-center justify-center rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-slate-500">
          <div>
            <ImageIcon className="mx-auto mb-3 h-10 w-10 text-slate-400" />
            <p>No map data is available from the database yet.</p>
          </div>
        </div>
      );
    }

    if (!activeMap.assetPath) {
      return (
        <div className="flex h-full items-center justify-center rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-slate-500">
          <div>
            <ImageIcon className="mx-auto mb-3 h-10 w-10 text-slate-400" />
            <p>No uploaded map image yet for this selection.</p>
          </div>
        </div>
      );
    }

    return (
      <div 
        ref={previewRefMain}
        className={`group relative h-[62vh] overflow-auto rounded-3xl border border-slate-200 bg-[radial-gradient(circle_at_top,rgba(251,191,36,0.12),transparent_40%),linear-gradient(135deg,#ffffff_0%,#f8fafc_100%)] p-4 shadow-inner ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMainMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <div className="min-w-full select-none" style={{ pointerEvents: isDragging ? 'none' : 'auto' }}>
          <ImageWithFallback
            src={encodeMapAssetPath(activeMap.assetPath)}
            alt={activeMap.subtitle || activeMap.title}
            className="block h-auto max-w-none rounded-2xl border border-slate-200 shadow-lg"
            style={{ width: `${zoom}%` }}
            fetchPriority="high"
            decoding="async"
          />
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,rgba(251,191,36,0.22),transparent_28%),radial-gradient(circle_at_bottom_right,rgba(16,185,129,0.14),transparent_28%),linear-gradient(180deg,#fffaf0_0%,#f8fafc_100%)] py-8">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-3 overflow-hidden rounded-3xl border border-amber-100 bg-white/90 shadow-[0_12px_35px_-22px_rgba(120,53,15,0.28)] backdrop-blur">
          <div className="flex flex-col gap-2 bg-gradient-to-r from-amber-950 via-amber-900 to-emerald-950 px-3.5 py-3.5 text-white lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <div className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-amber-100">
                <Sparkles className="h-3.5 w-3.5" />
                GIS Map Viewer
              </div>
              <h1 className="text-lg font-bold tracking-tight sm:text-xl">Cacao Land Suitability Maps</h1>
              <p className="mt-1 max-w-2xl text-xs leading-4 text-amber-100 sm:text-sm">
                Browse the GIS-exported suitability maps by municipality, province overview, cacao requirement factor, and sampling-site view. The viewer keeps the current route but replaces the old interactive map with the actual map outputs from the GIS folder.
              </p>
            </div>

            <div className="lg:w-[23rem]">
              {headerAction && (
                <div className="mb-2 flex justify-start lg:justify-end">
                  {headerAction}
                </div>
              )}
              <div className="grid grid-cols-2 gap-1.5">
                {mapStats.map((stat) => {
                  const Icon = stat.icon;

                  return (
                    <div key={stat.label} className="rounded-lg border border-white/15 bg-white/10 p-2 shadow-sm backdrop-blur-sm">
                      <div className="flex items-center gap-2">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/15 text-white">
                          <Icon className="h-3.5 w-3.5" />
                        </div>
                        <div>
                          <div className="text-sm font-bold leading-tight">{stat.value}</div>
                          <div className="text-[8px] text-amber-100/80">{stat.label}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="mb-6 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {TAB_DEFINITIONS.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.key;

            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`group rounded-3xl border p-4 text-left shadow-sm transition-all duration-200 ${
                  active
                    ? 'border-transparent bg-slate-900 text-white shadow-xl'
                    : 'border-slate-200 bg-white hover:-translate-y-0.5 hover:shadow-md'
                }`}
              >
                <div className={`mb-3 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br ${tab.accent} text-white shadow-lg`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="text-sm font-semibold">{tab.label}</div>
                <div className={`mt-1 text-xs leading-6 ${active ? 'text-slate-200' : 'text-slate-500'}`}>{tab.description}</div>
              </button>
            );
          })}
        </div>

        <div className="grid gap-6 lg:grid-cols-[360px_minmax(0,1fr)]">
          <aside className="flex min-h-[76vh] max-h-[80vh] flex-col overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-xl">
            <div className="border-b border-slate-200 bg-gradient-to-r from-slate-900 to-amber-950 px-5 py-5 text-white">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-amber-200">
                <Layers3 className="h-4 w-4" />
                Selection Panel
              </div>
              <h2 className="mt-2 text-lg font-semibold">{TAB_DEFINITIONS.find((tab) => tab.key === activeTab)?.label}</h2>
              <p className="mt-2 text-xs leading-6 text-amber-100/90">
                {TAB_DEFINITIONS.find((tab) => tab.key === activeTab)?.description}
              </p>
            </div>

            {activeTab === 'municipality' ? (
              <>
                <div className="border-b border-slate-200 p-4">
                  <div className="rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    Use the search bar to jump to a municipality. The viewer uses manual aliases for mismatched GIS folder names.
                  </div>
                </div>
                {renderSelectionList()}
              </>
            ) : (
              <>
                {activeTab === 'factor' && (
                  <div className="border-b border-slate-200 p-4">
                    <div className="rounded-2xl bg-sky-50 px-4 py-3 text-sm text-sky-900">
                      Ten cacao-requirement maps are available. Click any item to switch the preview.
                    </div>
                  </div>
                )}
                {activeTab === 'province' && (
                  <div className="border-b border-slate-200 p-4">
                    <div className="rounded-2xl bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                      Province view currently points to the Bohol-wide suitability export.
                    </div>
                  </div>
                )}
                {activeTab === 'sampling' && (
                  <div className="border-b border-slate-200 p-4">
                    <div className="rounded-2xl bg-violet-50 px-4 py-3 text-sm text-violet-900">
                      Sampling-site view shows the field locations used in the suitability analysis.
                    </div>
                  </div>
                )}
                {renderSelectionList()}
              </>
            )}
          </aside>

          <section className="space-y-6">
            <div className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-xl">
              <div className="flex flex-col gap-4 border-b border-slate-200 bg-slate-50 px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-amber-700">
                    <Info className="h-4 w-4" />
                    Current Map
                  </div>
                  <h2 className="mt-2 text-xl font-bold text-slate-900">{mapTitle}</h2>
                  <p className="mt-1 text-xs text-slate-600">{mapSubtitle}</p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm">
                    <Wind className="h-4 w-4 text-amber-600" />
                    Zoom {zoomLabel}
                  </div>
                  <div className="flex flex-nowrap items-center gap-2">
                    <Button variant="outline" onClick={zoomOut} className="rounded-full border-slate-200 bg-white px-4 py-2 text-slate-700 hover:bg-slate-50">
                      <ZoomOut className="mr-2 h-4 w-4" />
                      Out
                    </Button>
                    <Button variant="outline" onClick={zoomIn} className="rounded-full border-slate-200 bg-white px-4 py-2 text-slate-700 hover:bg-slate-50">
                      <ZoomIn className="mr-2 h-4 w-4" />
                      In
                    </Button>
                    <Button variant="outline" onClick={resetZoom} className="rounded-full border-slate-200 bg-white px-4 py-2 text-slate-700 hover:bg-slate-50">
                      <RotateCcw className="mr-2 h-4 w-4" />
                      Reset
                    </Button>
                    <Button onClick={() => setIsPreviewOpen(true)} className="rounded-full bg-amber-600 px-4 py-2 text-white hover:bg-amber-700">
                      <Maximize2 className="mr-2 h-4 w-4" />
                      Full Size
                    </Button>
                  </div>
                </div>
              </div>

              <div className="p-5">
                <div className="mb-4 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                  <span className="rounded-full bg-amber-50 px-3 py-1 font-medium text-amber-800">
                    {activeTab === 'municipality' ? 'Municipality' : activeTab === 'factor' ? 'Requirement' : activeTab === 'province' ? 'Province' : 'Sampling'}
                  </span>
                </div>

                {renderPreview()}

                <div className="mt-5 grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
                  <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                    <div className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Map Notes</div>
                    <p className="text-xs leading-7 text-slate-600">
                      This viewer uses the exported GIS images directly, so the map content matches the source layout, legend, and municipal labeling. The zoom control scales the image in the browser so users can inspect individual map sections without losing the original cartographic styling.
                    </p>
                  </div>

                  <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Active Selection</div>
                    <div className="space-y-3 text-xs text-slate-700">
                      <div>
                        <div className="text-xs uppercase tracking-[0.15em] text-slate-400">Name</div>
                        <div className="font-medium">{activeMap?.subtitle || activeMap?.title}</div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-[0.15em] text-slate-400">Mode</div>
                        <div className="font-medium capitalize">{activeTab}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {isPreviewOpen && activeMap && (
        <ModalShell
          title={activeMap.subtitle || activeMap.title}
          subtitle={activeMap.description}
          maxWidth="max-w-7xl"
          onClose={() => setIsPreviewOpen(false)}
          headerIcon={<ImageIcon className="h-5 w-5 text-amber-700" />}
        >
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm text-slate-600">Zoom {zoomLabel}</div>
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" onClick={zoomOut} className="rounded-full border-slate-200 bg-white px-4 py-2 text-slate-700 hover:bg-slate-50">
                  <ZoomOut className="mr-2 h-4 w-4" />
                  Out
                </Button>
                <Button variant="outline" onClick={zoomIn} className="rounded-full border-slate-200 bg-white px-4 py-2 text-slate-700 hover:bg-slate-50">
                  <ZoomIn className="mr-2 h-4 w-4" />
                  In
                </Button>
                <Button variant="outline" onClick={resetZoom} className="rounded-full border-slate-200 bg-white px-4 py-2 text-slate-700 hover:bg-slate-50">
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Reset
                </Button>
              </div>
            </div>

            <div 
              ref={previewRefModal}
              className={`max-h-[75vh] overflow-auto rounded-3xl border border-slate-200 bg-[radial-gradient(circle_at_top,rgba(251,191,36,0.1),transparent_35%),linear-gradient(135deg,#ffffff_0%,#f8fafc_100%)] p-4 ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
              onMouseDown={handleMouseDown}
              onMouseMove={handleModalMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              <div className="select-none" style={{ pointerEvents: isDragging ? 'none' : 'auto' }}>
                <ImageWithFallback
                  src={encodeMapAssetPath(activeMap.assetPath)}
                  alt={activeMap.subtitle || activeMap.title}
                  className="block h-auto max-w-none rounded-2xl border border-slate-200 shadow-lg"
                  style={{ width: `${zoom}%` }}
                  fetchPriority="low"
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </ModalShell>
      )}
    </div>
  );
}

export default React.memo(BoholMap);